import { useEffect, useState } from "react";
import { EMAIL_PROVIDER } from "@forgepoint/shared";
import type { EmailProvider } from "@forgepoint/shared";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ConnectionSelector } from "@/components/ConnectionSelector";
import { AgentEmailConnectionFields } from "./AgentEmailConnectionFields";
import { OutlookConnectionFields } from "./OutlookConnectionFields";

/** Values managed by the email configuration sheet */
export interface EmailConfigValues {
  emailProvider: EmailProvider | null;
  forgepointEmailPrefix: string;
  gmailConnectionId: string | null;
  gmailFromEmail: string;
  outlookConnectionId: string | null;
  outlookSharedFolderEmail: string;
  outlookSharedFolderId: string;
  agentEmailConnectionId: string | null;
  agentEmailFromAddress: string;
  smtpConnectionId: string | null;
  smtpReplyLookupImapConnectionId: string | null;
}

interface EmailConfigSheetProps {
  /** Whether the sheet is open */
  open: boolean;
  /** Callback when the open state changes */
  onOpenChange: (open: boolean) => void;
  /** Current email configuration values used to initialize the sheet */
  initialValues: EmailConfigValues;
  /** Callback invoked with updated values when the user clicks Apply */
  onApply: (values: EmailConfigValues) => void;
  /** Organization slug used for Forgepoint Mail email preview */
  organizationSlug?: string;
}

const normalizeOptionalDynamicString = (value?: string | null): string => {
  if (!value || value === "undefined") {
    return "";
  }
  return value;
};

/**
 * Sheet for configuring the agent's email provider and related connection settings.
 * Changes are applied only when the user confirms.
 * @param props - EmailConfigSheetProps
 * @returns Sheet element for email configuration
 */
export function EmailConfigSheet({
  open,
  onOpenChange,
  initialValues,
  onApply,
  organizationSlug,
}: EmailConfigSheetProps) {
  const [provider, setProvider] = useState<EmailProvider | null>(null);
  const [fpPrefix, setFpPrefix] = useState("");
  const [gmailConnId, setGmailConnId] = useState<string | null>(null);
  const [gmailFrom, setGmailFrom] = useState("");
  const [outlookConnId, setOutlookConnId] = useState<string | null>(null);
  const [outlookFolderEmail, setOutlookFolderEmail] = useState("");
  const [outlookFolderId, setOutlookFolderId] = useState("");
  const [agentEmailConnId, setAgentEmailConnId] = useState<string | null>(null);
  const [agentEmailFrom, setAgentEmailFrom] = useState("");
  const [smtpConnId, setSmtpConnId] = useState<string | null>(null);
  const [smtpImapConnId, setSmtpImapConnId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    setProvider(initialValues.emailProvider);
    setFpPrefix(initialValues.forgepointEmailPrefix);
    setGmailConnId(initialValues.gmailConnectionId);
    setGmailFrom(initialValues.gmailFromEmail);
    setOutlookConnId(initialValues.outlookConnectionId);
    setOutlookFolderEmail(
      normalizeOptionalDynamicString(initialValues.outlookSharedFolderEmail),
    );
    setOutlookFolderId(
      normalizeOptionalDynamicString(initialValues.outlookSharedFolderId),
    );
    setAgentEmailConnId(initialValues.agentEmailConnectionId);
    setAgentEmailFrom(
      normalizeOptionalDynamicString(initialValues.agentEmailFromAddress),
    );
    setSmtpConnId(initialValues.smtpConnectionId);
    setSmtpImapConnId(initialValues.smtpReplyLookupImapConnectionId);
  }, [open, initialValues]);

  const handleApply = () => {
    onApply({
      emailProvider: provider,
      forgepointEmailPrefix: fpPrefix,
      gmailConnectionId: gmailConnId,
      gmailFromEmail: gmailFrom,
      outlookConnectionId: outlookConnId,
      outlookSharedFolderEmail: outlookFolderEmail,
      outlookSharedFolderId: outlookFolderId,
      agentEmailConnectionId: agentEmailConnId,
      agentEmailFromAddress: agentEmailFrom,
      smtpConnectionId: smtpConnId,
      smtpReplyLookupImapConnectionId: smtpImapConnId,
    });
    onOpenChange(false);
  };

  const orgSlug =
    organizationSlug && organizationSlug.length > 0 ? organizationSlug : "org";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:w-[540px] sm:max-w-[540px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Email Configuration</SheetTitle>
          <SheetDescription>
            Choose the email provider the agent uses to send and receive emails.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-1.5">
            <Label htmlFor="sheet-email-provider">Email Provider</Label>
            <Select
              value={provider ?? "none"}
              onValueChange={(v) =>
                setProvider(v === "none" ? null : (v as EmailProvider))
              }
            >
              <SelectTrigger id="sheet-email-provider" className="w-full">
                <SelectValue placeholder="Select provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value={EMAIL_PROVIDER.FORGEPOINT_MAIL}>
                  Forgepoint Mail
                </SelectItem>
                <SelectItem value={EMAIL_PROVIDER.GMAIL}>Gmail</SelectItem>
                <SelectItem value={EMAIL_PROVIDER.MICROSOFT_OUTLOOK}>
                  Microsoft Outlook
                </SelectItem>
                <SelectItem value={EMAIL_PROVIDER.AGENT_EMAIL}>
                  Agent Email (Outlook)
                </SelectItem>
                <SelectItem value={EMAIL_PROVIDER.SMTP}>SMTP</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {provider === EMAIL_PROVIDER.FORGEPOINT_MAIL && (
            <div className="space-y-1.5">
              <Label htmlFor="sheet-fp-prefix">Email Prefix</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="sheet-fp-prefix"
                  value={fpPrefix}
                  onChange={(e) => setFpPrefix(e.target.value)}
                  placeholder="prefix"
                />
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  -{orgSlug}@m.forgepoint.ai
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Result: {fpPrefix || "prefix"}-{orgSlug}@m.forgepoint.ai
              </p>
            </div>
          )}

          {provider === EMAIL_PROVIDER.GMAIL && (
            <>
              <div className="space-y-1.5">
                <Label>Gmail Connection</Label>
                <ConnectionSelector
                  appId="gmail"
                  providerId="pipedream"
                  connectionId={gmailConnId}
                  onSelect={setGmailConnId}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sheet-gmail-from">
                  Send-As Alias (optional)
                </Label>
                <Input
                  id="sheet-gmail-from"
                  value={gmailFrom}
                  onChange={(e) => setGmailFrom(e.target.value)}
                  placeholder="alias@yourdomain.com"
                />
              </div>
            </>
          )}

          {provider === EMAIL_PROVIDER.MICROSOFT_OUTLOOK && (
            <OutlookConnectionFields
              connectionId={outlookConnId}
              onConnectionIdChange={setOutlookConnId}
              sharedFolderEmail={outlookFolderEmail}
              onSharedFolderEmailChange={setOutlookFolderEmail}
              sharedFolderId={outlookFolderId}
              onSharedFolderIdChange={setOutlookFolderId}
            />
          )}

          {provider === EMAIL_PROVIDER.AGENT_EMAIL && (
            <AgentEmailConnectionFields
              connectionId={agentEmailConnId}
              onConnectionIdChange={setAgentEmailConnId}
              fromAddress={agentEmailFrom}
              onFromAddressChange={setAgentEmailFrom}
            />
          )}

          {provider === EMAIL_PROVIDER.SMTP && (
            <>
              <div className="space-y-1.5">
                <Label>SMTP Connection</Label>
                <ConnectionSelector
                  appId="imap"
                  providerId="pipedream"
                  connectionId={smtpConnId}
                  onSelect={setSmtpConnId}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Reply Lookup IMAP Connection</Label>
                <ConnectionSelector
                  appId="imap"
                  providerId="pipedream"
                  connectionId={smtpImapConnId}
                  onSelect={setSmtpImapConnId}
                />
                <p className="text-xs text-muted-foreground">
                  Used to resolve the original message when replying by
                  Message-ID.
                </p>
              </div>
            </>
          )}
        </div>

        <SheetFooter className="pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleApply}>Apply</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
